import { css } from "@emotion/css"
import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  addToast,
} from "@heroui/react"
import { Trans, useLingui } from "@lingui/react/macro"
import { clsx } from "clsx"
import { Clipboard } from "lucide-react"

import { KVTable } from "#/components/KVTable"

import { InitiatorView } from "../components/Initiator"
import { FetchStyle, generateAxiosCall, generateFetchCall } from "../copy"
import type { Entry } from "../har"
import { hasSFLetterSpacingBug } from "../utils"

import { PostData } from "./PostData"
import { URLView } from "./RequestURL"

export function RequestTab({ entry }: { entry?: Entry }) {
  const { t } = useLingui()
  if (!entry) {
    return null
  }

  const { request, _initiator } = entry

  return (
    <div css="m-3 mb-6">
      <div css="m-1 mb-4 flex items-center gap-2">
        <Chip css="select-none">{request.method}</Chip>{" "}
        <div
          css="max-h-52 overflow-scroll text-wrap break-all text-base"
          className={clsx(
            "scroll-fade",
            css`
              font-family: "SF Pro Rounded", var(--font-sans);
              letter-spacing: ${hasSFLetterSpacingBug ? 0.4 : 0}px;
            `
          )}
        >
          <URLView url={request.url} />
        </div>
      </div>
      {_initiator && <InitiatorView initiator={_initiator} />}
      <PostData postData={request.postData} />
      {request.queryString.length > 0 && (
        <KVTable
          sort
          aria-label={t`Query string`}
          css="mx-1 mb-6"
          title={t`Query string`}
          values={request.queryString}
          valueCellClassName={css`
            font-family: "SF Pro Rounded", var(--font-sans);
            letter-spacing: ${hasSFLetterSpacingBug ? 0.5 : 0}px;
          `}
        />
      )}
      <KVTable
        sort
        aria-label={t`Headers`}
        css="mx-1"
        emptyContent={t`No headers`}
        title={t`Headers`}
        values={request.headers}
      />

      <div css="mx-1 my-4 flex gap-2">
        <Dropdown>
          <DropdownTrigger>
            <Button css="mt-1 border-[0.5px]" variant="bordered">
              <Clipboard size={17} />
              <Trans>Copy</Trans>
            </Button>
          </DropdownTrigger>
          <DropdownMenu>
            <DropdownItem
              key="copy-as-fetch"
              onPress={async () => {
                const call = generateFetchCall(request, FetchStyle.BROWSER)
                await navigator.clipboard.writeText(call)
                addToast({
                  title: t`Copied to clipboard`,
                  description: t`Successfully copied to clipboard`,
                  color: "success",
                })
              }}
            >
              <Trans>Copy as fetch</Trans>
            </DropdownItem>
            <DropdownItem
              key="copy-as-axios"
              onPress={async () => {
                const call = generateAxiosCall(request, FetchStyle.BROWSER)
                await navigator.clipboard.writeText(call)
                addToast({
                  title: t`Copied to clipboard`,
                  description: t`Successfully copied to clipboard`,
                  color: "success",
                })
              }}
            >
              <Trans>Copy as axios</Trans>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  )
}
